<template>
  <v-app :class="{ mobile: $vuetify.breakpoint.xsOnly }">
    <AppBar
      app-name="my-nsw-gov"
      :title="$t($route.meta.title)"
      :search-filter-mutator="false"
      :show-full-screen="false"
      :show-nav-icon="$route.meta.showNavigation"
      :enable-home-link="false"
      :color="$route.name == 'Home' ? '#CBEDFD' : 'primary'"
    >
      <template
        v-if="!$route.meta.showNavigation"
        slot="title"
      >
        <span class="title--width">
          <v-toolbar-title
            aria-label="Welcome"
            tabindex="0"
            v-if="$route.name == 'Welcome'"
          >
            <div class="appbar-title px-6">
              <h1>Welcome</h1>
            </div>
          </v-toolbar-title>
          <v-toolbar-title
            aria-label="Welcome"
            tabindex="0"
            v-if="$route.name == 'Home'"
          >
            <div class="appbar-title px-5">
              <v-img
                src="/img/doe-logo.svg"
                max-height="39px"
                max-width="39px"
              />
            </div>
          </v-toolbar-title>
        </span>
      </template>
      <template slot="myEssentials">
        <div class="d-flex">
          <span v-if="$route.name == 'News'">
            <v-btn
              icon
              @click="personalisation = true"
            >
              <v-icon>mdi-filter-variant</v-icon>
            </v-btn>
            <FullscreenDialog
              title=""
              v-model="personalisation"
            >
              <template slot="content">
                <NewsFilter />
              </template>
            </FullscreenDialog>
          </span>
          <span v-if="$route.name == 'Home'">
            <v-btn
              icon
              @click="$router.push('/settings')"
            >
              <v-icon color="primary">mdi-cog-outline</v-icon>
            </v-btn>
          </span>
          <span>
            <AppBarFilters
              v-if="$route.meta.showFilter"
              :my-schools="mySchools"
              @applyFilter="applyFilter"
              :app-filters="appFilters[getFilterKey]"
              :title="getFilterTitle"
              :filter-key="getFilterKey"
            />
            <SchoolSwitcher
              v-if="$route.meta.showSwitcher && mySchools.length > 1"
              :my-schools="mySchools"
              @applyFilter="applyFilter"
              :app-filters="appFilters[getFilterKey]"
              :title="getFilterTitle"
            />
          </span>
          <span v-if="$route.meta.showNotifications">
            <AppBarNotifications
              :my-schools="mySchools"
              @applyFilter="applyFilter"
              :app-filters="appFilters[getFilterKey]"
              :title="getFilterTitle"
              :notifications-key="getNotificationsKey"
            />
          </span>
        </div>
      </template>
    </AppBar>
    <div class="school-switcher-container" />
    <v-main>
      <v-container
        :class="this.isWide ? 'pa-0' : 'px-5'"
        :fluid="!this.isWide"
      >
        <v-fade-transition mode="out-in">
          <router-view />
        </v-fade-transition>
      </v-container>
    </v-main>
    <NavigationDrawer
      :items="
        user && user != 'error'
          ? navigationItems.loggedInItems
          : navigationItems.items
      "
      :show-profile-mobile="user && user != 'error' ? true : false"
      :profile-name="
        user && user != 'error' ? `${user.given_name} ${user.family_name}` : ''
      "
    />
    <BlockUI
      v-if="$store.getters.isLoading"
      :text="$t('general.loading')"
    />
  </v-app>
</template>
<script>
import {
  AppBar,
  DataSwitcher,
  NavigationDrawer,
  AppIcon,
  BlockUI,
  FullscreenDialog,
  AdsButton
} from "@nswdoe/doe-ui-core";
import { mapGetters } from "vuex";
import AppBarFilters from "./components/AppBarFilters";
import NewsFilter from "./components/NewsFilter";
import SchoolSwitcher from "./components/SchoolSwitcher";
import AppBarNotifications from "./components/AppBarNotifications";
import { analytics } from "@/mixins/analytics";
import i18n from "@/i18n";

const App = {
  name: "App",
  components: {
    BlockUI,
    AppBar,
    AppBarFilters,
    NewsFilter,
    DataSwitcher,
    AppIcon,
    NavigationDrawer,
    FullscreenDialog,
    AdsButton,
    SchoolSwitcher,
    AppBarNotifications
  },
  computed: {
    ...mapGetters(["isLoading", "schools", "mySchools", "appFilters", "user"]),
    navigationItems() {
      return {
        items: [
          {
            title: this.$t("settings.snsw.signInWithServiceNSW"),
            icon: "mdi-logout-variant",
            iconOutlined: true,
            clickHandler: this.login
          },
          {
            title: this.$t("settings.title"),
            icon: "settings",
            iconOutlined: true,
            route: "/settings",
            id: "settings"
          },
          {
            title: this.$t("home.title"),
            icon: "mdi-home-outline",
            iconOutlined: true,
            route: "/",
            exact: true
          },
          {
            title: this.$t("news.title"),
            icon: "library_books",
            iconOutlined: true,
            route: "/news"
          },
          // {
          //   title:"School Essentials",icon:"apps",
          //   route:"/essentials"
          // },
          {
            iconOutlined: true,
            title: this.$t("calendar.title"),
            icon: "calendar_today",
            route: "/events"
          },
          {
            title: this.$t("newsletters.title"),
            icon: "mdi-email-open-outline",
            route: "/newsletters"
          },
          {
            title: this.$t("enrolment.title"),
            icon: "mdi-bank-outline",
            route: "/enrolment"
          },
          {
            title: this.$t("learningResources.title"),
            icon: "mdi-lightbulb-outline",
            route: "/learning-resources"
          },
          {
            title: this.$t("mySchool.title"),
            icon: "school",
            iconOutlined: true,
            items: this.mySchools
              ? this.mySchools.map(school => {
                  return {
                    title: school.details.name,
                    route: "/school/" + school.schoolId,
                    icon: "library_books"
                  };
                })
              : null
          }
        ],
        loggedInItems: [
          {
            title: this.$t("settings.title"),
            icon: "settings",
            iconOutlined: true,
            route: "/settings"
          },
          {
            title: this.$t("settings.snsw.signout"),
            icon: "mdi-logout-variant",
            iconOutlined: true,
            clickHandler: this.logout,
            id: "settings"
          },
          {
            title: this.$t("home.title"),
            icon: "mdi-home-outline",
            iconOutlined: true,
            route: "/",
            exact: true
          },
          {
            title: this.$t("news.title"),
            icon: "library_books",
            iconOutlined: true,
            route: "/news"
          },
          // {
          //   title:"School Essentials",icon:"apps",
          //   route:"/essentials"
          // },
          {
            iconOutlined: true,
            title: this.$t("calendar.title"),
            icon: "calendar_today",
            route: "/events"
          },
          {
            title: this.$t("newsletters.title"),
            icon: "mdi-email-open-outline",
            route: "/newsletters"
          },
          {
            title: this.$t("enrolment.title"),
            icon: "mdi-bank-outline",
            route: "/enrolment"
          },
          {
            title: this.$t("learningResources.title"),
            icon: "mdi-lightbulb-outline",
            route: "/learning-resources"
          },
          {
            title: this.$t("mySchool.title"),
            icon: "school",
            iconOutlined: true,
            items: this.mySchools
              ? this.mySchools.map(school => {
                  return {
                    title: school.details.name,
                    route: "/school/" + school.schoolId,
                    icon: "library_books"
                  };
                })
              : null
          }
        ]
      };
    },
    isWide() {
      return ["Home", "Learning resources"].indexOf(this.$route.name) > -1;
    },
    getFilterKey() {
      let route = this.$route.path;
      switch (route) {
        case "/news":
          return "newsFilter";
        case "/events":
          return "eventFilter";
        case "/newsletters":
          return "newsletterFilter";
      }
    },
    getNotificationsKey() {
      let route = this.$route.name;
      switch (route) {
        case "News":
          return "newsNotifications";
        case "Calendar":
          return "eventNotifications";
        case "Newsletters":
          return "newsletterNotifications";
      }
    },
    getFilterTitle() {
      switch (this.getFilterKey) {
        case "newsFilter":
          return "Show news items for:";
        case "eventFilter":
          return this.$t("calendar.overlayShowEvents");
        case "newsletterFilter":
          return this.$t("newsletters.overlayShowNewsletters");
      }
    },
    appBuild() {
      return this.getCookie("appbuild");
    }
  },
  created() {
    this.$store.dispatch("setUserRetries", 0);
    this.$store.dispatch("setAppID");
    this.$store.dispatch("registerAllSchools");
    this.$store.dispatch("fetchSchoolData");
    this.$store.dispatch("fetchSchools");
    this.$store.dispatch("registerApp");
    this.$store.dispatch("fetchUserData");
    this.$store.dispatch("fetchNotifications");
  },
  data: function() {
    return {
      filterMenu: false,
      personalisation: false
    };
  },
  methods: {
    i18n() {
      return i18n;
    },
    applyFilter(filter) {
      let filterKey = this.getFilterKey;
      this.$store.dispatch("setAppFilter", { key: filterKey, value: filter });
    },
    login() {
      //eslint-disable-next-line
      NativeJS.debugLog(`Logging in`);
      analytics("my_account", { sign_in: "tap" });
      //eslint-disable-next-line
      NativeJS.login();
    },
    logout() {
      //eslint-disable-next-line
      NativeJS.debugLog(`Logging out`);
      analytics("my_account", { sign_out: "success" });
      //eslint-disable-next-line
      NativeJS.logout();
      this.$store.dispatch("logout");
    }
  }
};

export default App;
</script>

<style lang="scss">
@import "./scss/ads.scss";

.title--width {
  max-width: 70%;
}

.appbar-title {
  vertical-align: middle;

  h1 {
    font-size: 1.25rem;
    font-weight: 700;
  }
}

.container {
  padding: 12px 30px;

  .container {
    padding: 0;
  }
}

.mobile {
  .container {
    padding: 12px;
  }
  .container > .container {
    padding: 0;
  }
}
#settings {
  margin-bottom: 16px !important;
  &:after {
    position: absolute;
    background-color: #e4e4e4;
    height: 1px;
    bottom: -10px;
    left: -9px;
    right: -9px;
    min-height: 0;
  }
}
.navigation-drawer-mobile {
  border-bottom: none !important;
  margin-bottom: 0 !important;
  .v-expansion-panel-header {
    pointer-events: none;
    padding-bottom: 0;
    .v-expansion-panel-header__icon {
      display: none;
    }
  }
  .v-expansion-panel-content {
    display: flex !important;
  }
}
.v-application .v-toolbar .navigation-drawer-toggle {
  margin-left: 8px !important;
}
.v-dialog--fullscreen {
  .v-toolbar {
    background-color: #002664 !important;
  }
}
.school-switcher-container {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 10000000;
  max-height: 100%;
  overflow-y: auto;
  padding-top: 12px;
  padding-right: 8px;
  padding-bottom: 5px;
  padding-left: 20px;
  .v-menu__content {
    position: initial;
    max-width: none;
  }
}
</style>
